import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import DataTable from "../components/DataTable";
import RegisterCatalog from "../components/RegisterCatalog";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import * as Utils from "../utils/utils";
import * as Common from "../commons/common";

type DataIndex = keyof Types.CatalogDto;

function CatalogList() {
    const alert = useAlert();

    const [catalogs, setCatalogs] = useState<Array<Types.CatalogDto>>();
    const [currentCatalog, setcurrentCatalog] = useState<Types.CatalogDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteCatalog, setOpenDeleteCatalog] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.CatalogDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.CatalogDto> = [
        {
            align: "center",
            title: String.catalogId,
            dataIndex: "catalogId",
            key: "catalogId",
            ...getColumnSearchProps("catalogId"),
        },
        {
            align: "center",
            title: String.catalogVersion,
            dataIndex: "catalogVersion",
            key: "catalogVersion",
        },
        {
            align: "center",
            title: String.catalogName,
            dataIndex: "catalogName",
            key: "catalogName",
            ...getColumnSearchProps("catalogName"),
        },
        {
            align: "center",
            title: String.catalogFileName,
            dataIndex: "catalogFileName",
            key: "catalogFileName",
            render: (_, { catalogId, catalogFileName }) => {
                return (
                    <a
                        onClick={() => {
                            handleDownloadCatalogOk(catalogId, catalogFileName);
                        }}
                    >
                        {catalogFileName}
                    </a>
                );
            },
        },
    ];

    const requestAxiosGetCatalogs = async () => {
        const response = await utilAxiosWithAuth().get(Request.CATALOG_LIST_URL);
        console.log(response.data);
        return response.data;
    };

    const {
        loading: loadingGetCatalogs,
        error: errorGetCatalogs,
        data: resultGetCatalogs,
        execute: requestGetCatalogs,
    } = useAsyncAxios(requestAxiosGetCatalogs);

    useEffect(() => {
        if (resultGetCatalogs === null) return;

        setCatalogs(resultGetCatalogs.catalogs);
    }, [resultGetCatalogs]);

    useEffect(() => {
        if (errorGetCatalogs === null) return;

        alert.setAlert(errorGetCatalogs.response.data.code, "Error", errorGetCatalogs.response.data.message);
    }, [errorGetCatalogs]);

    const requestAxiosRemoveCatalog = async (data: Types.CatalogDto) => {
        const response = await utilAxiosWithAuth().delete(Request.CATALOG_URL + "/" + data.catalogId);
        return response.data;
    };

    const {
        loading: loadingRemoveCatalog,
        error: errorRemoveCatalog,
        data: resultRemoveCatalog,
        execute: requestRemoveCatalogs,
    } = useAsyncAxios(requestAxiosRemoveCatalog);

    useEffect(() => {
        if (resultRemoveCatalog === null) return;

        alert.setAlert(AlertType.SUCCESS, "카탈로그 삭제 성공", "카탈로그 삭제를 완료하였습니다.");

        requestGetCatalogs();
    }, [resultRemoveCatalog]);

    useEffect(() => {
        if (errorRemoveCatalog === null) return;

        alert.setAlert(errorRemoveCatalog.response.data.code, "Error", errorRemoveCatalog.response.data.message);
    }, [errorGetCatalogs]);

    useEffect(() => {
        requestGetCatalogs();
    }, []);

    const onRow = (value: Types.CatalogDto) => {
        setEditMode("none");
        setcurrentCatalog(value);
    };

    const handleDeleteCatalog = () => {
        if (currentCatalog !== undefined) setOpenDeleteCatalog(true);
    };

    const handleDeleteCatalogOk = () => {
        setOpenDeleteCatalog(false);
        requestRemoveCatalogs(currentCatalog);
    };

    const handleDeleteCatalogCancel = () => {
        setOpenDeleteCatalog(false);
    };

    const handleDownloadCatalogOk = (catalogId: number, catalogFileName: string) => {
        console.log("id", catalogId);

        const token = JSON.parse(Utils.getLocalStorage(Common.CONTEXT_AUTH) || "{}").token;

        fetch(Request.CATALOG_DOWNLOAD_URL + "/" + catalogId, {
            headers: {
                "Content-Type": "application/octet-stream",
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => {
            response.arrayBuffer().then((arrayBuffer) => {
                saveByteArray(catalogFileName, new Uint8Array(arrayBuffer));
            });
        });
    };

    function saveByteArray(fileName: any, byte: any) {
        const blob = new Blob([byte], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    }

    const onRegisterCatalog = () => {
        requestGetCatalogs();
        setEditMode("none");
    };

    return (
        <>
            <Row justify="space-between">
                <Col span={6}>
                    <Divider orientation="left">
                        <span>카탈로그</span>
                    </Divider>
                </Col>
                <Space>
                    <Button
                        onClick={() => {
                            editMode === "add" ? setEditMode("none") : setEditMode("add");
                        }}
                    >
                        {String.userAdd}
                    </Button>
                    <Button
                        onClick={() => {
                            editMode === "update" ? setEditMode("none") : setEditMode("update");
                        }}
                    >
                        {String.userUpdate}
                    </Button>
                    <Button onClick={() => handleDeleteCatalog()} danger>
                        {String.userRemove}
                    </Button>
                </Space>
            </Row>
            <DataTable hasScroll={false} columns={columns} rowKey="catalogId" dataSource={catalogs} onRow={onRow} />
            {editMode === "add" && <RegisterCatalog editMode={editMode} onRegisterCatalog={onRegisterCatalog} />}

            {editMode !== "add" && currentCatalog !== undefined && (
                <RegisterCatalog editMode={editMode} catalog={currentCatalog} onRegisterCatalog={onRegisterCatalog} />
            )}

            {openDeleteCatalog && (
                <Modal open={openDeleteCatalog} onOk={handleDeleteCatalogOk} onCancel={handleDeleteCatalogCancel}>
                    {currentCatalog?.catalogName} 삭제하시겠습니까?
                </Modal>
            )}
        </>
    );
}

export default CatalogList;
